import { selectProfile } from '@store/profile.reducer'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

export function useCurrentHelper() {
  const STORAGE_KEY = 'hideAdminSupportHelper'
  const location = useLocation()
  const profile = useSelector(selectProfile)
  const [lastRole, setLastRole] = useState(profile?.role)
  const [helperString, setHelperString] = useState<string | null>(null)
  const [isVisible, setIsVisible] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY)
    return saved !== null ? JSON.parse(saved) : true
  })

  const currentHelper = useMemo(() => {
    if (!isVisible) return null

    const currentPath = location.pathname
    if (lastRole !== profile.role) {
      setLastRole(profile.role)
      if (
        [
          'seminarian',
          'senior-seminarian',
          'personal-manager',
          'hr-manager',
          'parent',
        ].includes(profile.role)
      ) {
        return 'Подробнее о том, как настраивать роли на платформе [рассказали тут](https://rutube.ru/video/02134dc1b3c7eaa391908b500653108f/?r=wd)'
      }
      if (['children'].includes(profile.role)) {
        return 'Это личный кабинет учащегося, вот [видеоинструкция по работе](https://rutube.ru/video/15a478dc2e6beb124cbee349e56e0e97/?r=wd) с ним'
      }
    }
    if (currentPath === '' || currentPath === '/') {
      return `Привет, ${profile?.name}! Вот [видеокурс](https://impulse-lms.com/tutorial/) по основным функциям платформы`
    }
    return (
      routes.find((route) =>
        route.path.some((item) => location.pathname?.includes(item)),
      )?.text || null
    )
  }, [location.pathname, routes, profile.role, isVisible])

  useEffect(() => {
    setHelperString(null)
    const timeout = setTimeout(() => {
      setHelperString(currentHelper)
    }, 400)

    return () => clearTimeout(timeout)
  }, [currentHelper])

  const onChangeVisibility = (visible: boolean, save: boolean = false) => {
    if (visible) {
      setHelperString(currentHelper)
    } else {
      setHelperString(null)
    }
    if (save) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(visible))
      setIsVisible(visible)
    }
  }

  return { message: helperString, onChangeVisibility }
}

const DefaultMessage =
  'Видео пока в разработке, но Вы можете написать нам в тех.поддержку'

const routes = [
  {
    path: ['/platform-settings'],
    text: 'Вот [видеокурс](https://impulse-lms.com/tutorial/) по основным функциям платформы',
  },
  {
    path: ['/direction-editor'],
    text: 'Это страница редактирования курсов, вот [видеоинструкция по работе](https://rutube.ru/video/b3d9299a5a87f65e46bf64873477b6fc/?r=plemwd) с ней',
  },
  {
    path: ['/courses/all'],
    text: 'Это страница настройки каталога, вот [видеоинструкция по работе](https://rutube.ru/video/db7834a60f87b01d2dcec6709f9b5385/?r=wd) с ней',
  },
  {
    path: ['/courses/my'],
    text: 'Вы можете посмотреть готовый курс, вот [видеоинструкция](https://impulse-synergyhub.ru/courses-ch) как это сделать',
  },
  {
    path: ['/edit-direction/'],
    text: 'Это страница с материалами курса, вот [видеоинструкция по работе](https://rutube.ru/video/b3d9299a5a87f65e46bf64873477b6fc/?r=wd) с ней, [создание заданий](https://rutube.ru/video/e15580e71a573929195b0611ce93e441/?r=wd), [просмотр готового курса](https://rutube.ru/video/e26f72867a0dea01e13f214b5ebc2ef8/?r=wd)',
  },
  {
    path: ['/v2/direction-editor/theme'],
    text: 'Это страница с созданием заданий, вот [видеоинструкция по работе](https://rutube.ru/video/e15580e71a573929195b0611ce93e441/?r=wd) с ней, [загрузка материала](https://rutube.ru/video/b3d9299a5a87f65e46bf64873477b6fc/?r=wd), [просмотр готового курса](https://rutube.ru/video/e26f72867a0dea01e13f214b5ebc2ef8/?r=wd)',
  },
  {
    path: ['/surveys'],
    text: 'Это страница с опросами, вот [видеоинструкция по работе](https://rutube.ru/video/0652572ff7ec67fdb4c9aabe45c67919/?r=wd) с ней',
  },
  {
    path: ['/courses-check'],
    text: 'Вы можете посмотреть готовый курс, вот [видеоинструкция как это сделать](https://rutube.ru/video/e26f72867a0dea01e13f214b5ebc2ef8/?r=wd)',
  },
  {
    path: ['/users/colleges'],
    text: 'Это страница с карточками сотрудников, вот [видеоинструкция по работе](https://rutube.ru/video/832046d55dc9c03f28034e6a11cc0aef/?r=wd) с ней',
  },
  {
    path: ['/new-students'],
    text: DefaultMessage,
  },
  {
    path: ['/direction-editor/direction'],
    text: 'Это страница для открытия тем по времени, вот [видеоинструкция по работе](https://rutube.ru/video/15ff4bbb2d9846b748960ef47deffc7b/?r=wd) с ней',
  },
  {
    path: ['/users/students'],
    text: 'Это страница с карточками учащихся, вот [видеоинструкция по работе](https://rutube.ru/video/e4a7ff6846709cc74926f7486c7feb07/?r=wd) с ней',
  },
  {
    path: ['/task-bank'],
    text: 'Это страница с банком и архивом задач, вот [видеоинструкция по работе](https://rutube.ru/video/e15580e71a573929195b0611ce93e441/?r=wd) с ней',
  },
  {
    path: ['/platform-media'],
    text: DefaultMessage,
  },
  {
    path: ['/reports/group'],
    text: DefaultMessage,
  },
  {
    path: ['/news'],
    text: DefaultMessage,
  },
  {
    path: ['/journal'],
    text: DefaultMessage,
  },
  {
    path: ['/learning-base'],
    text: DefaultMessage,
  },
  {
    path: ['/notifi/mailings'],
    text: DefaultMessage,
  },
  {
    path: ['/communication'],
    text: DefaultMessage,
  },
  {
    path: ['/requests'],
    text: DefaultMessage,
  },
  {
    path: ['/teams'],
    text: DefaultMessage,
  },
  {
    path: ['/dz-students/dz'],
    text: DefaultMessage,
  },
  {
    path: ['/admin/banner-management'],
    text: DefaultMessage,
  },
  {
    path: ['/reports/private-owners'],
    text: DefaultMessage,
  },
  {
    path: ['/manage/classes'],
    text: 'Настройка групп и классов – [Смотреть](https://rutube.ru/video/f204e1c909efd7ef2bf3a14a26adb4e8/?r=wd)',
  },
  {
    path: ['/video-lesson'],
    text: 'Записи занятий – [Смотреть](https://rutube.ru/video/05b8ac010492b459b3e66b684e14c35c/?r=wd)',
  },
  {
    path: ['/course-analytics/'],
    text: 'Динамика и аналитика – [Смотреть](https://rutube.ru/video/0cc01ce29d6728ade67e3bef3bd3a01d/?r=wd)',
  },
  {
    path: ['/library', '/departments'],
    text: 'Отделы и регламенты – [Смотреть](https://rutube.ru/video/96ab4619398e5790c3b9ec944d99b910/?r=wd)',
  },
  {
    path: ['/reports/sales'],
    text: 'Отчет ДДС и P&L – [Смотреть](https://rutube.ru/video/462c7325d21823c18c4a362bb2b814b7/?r=wd)',
  },
  {
    path: ['/reports/debtor-payment'],
    text: 'Должники и ручные оплаты – [Смотреть](https://rutube.ru/video/4168564f8782e65e15e277eac3182a81/?r=wd)',
  },
]
